<template>
  <vue-apex-charts ref="chart" :options="chartOptions" :series="yAxisSeries"></vue-apex-charts>
</template>
<script>

import VueApexCharts from 'vue-apexcharts'
import ResourceTypeEnum from "../../ResourceTypeEnum.js"
import MiningTypeEnum from "../../MiningTypeEnum.js"
import ResourceCategoryEnum from '../../ResourceCategoryEnum.js';
import ElementEnum from '@/app/shared/ElementEnum.js';

export default {
  components: {
    VueApexCharts
  },
  props: {
    considerOwnershipPercentage: {
      type: Boolean,
      default: false
    },
    selectedStatement: {
      type: Object,
      default: () => null
    },
    comparisonStatement: {
      type: Object,
      default: () => null
    },
    detailedDiagram: {
      type: Boolean,
      default: false
    },
    openPitSelected: {
      type: Boolean,
      default: false
    },
    undergroundSelected: {
      type: Boolean,
      default: false
    },
    element: {
      type: ElementEnum,
      default: ElementEnum.Gold
    },
    yAxisLabel: {
      type: String,
      default: ''
    }
  },
  computed: {
    comparisonStatementName() {
      if (this.comparisonStatement)
        return this.comparisonStatement.periodName;
      else
        return "";
    }
  },
  watch: {
    selectedStatement() {
      this.refreshDiagram();
    },
    comparisonStatement() {
      this.refreshDiagram();
    },
    detailedDiagram() {
      this.refreshDiagram();
    },
    undergroundSelected() {
      this.refreshDiagram();
    },
    openPitSelected() {
      this.refreshDiagram();
    },
    element() {
      this.refreshDiagram();
    },
    considerOwnershipPercentage() {
      this.refreshDiagram();
    }
  },
  data() {
    return {
      chartOptions: {
        colors: ['#666666', '#0099cc', '#000000', '#00cc00', '#ff0000', '#FFD700'],
        chart: {
        },
        xaxis: {
          categories: []
        },
        yaxis: [],
        stroke: {
          width: [4, 4, 2, 2, 2],
          curve: ['straight', 'straight',  'straight', 'straight', 'straight'],
          dashArray: [0, 0, 5, 5, 5]
        },
        markers: {
          size: [0, 0, 0, 0, 0, 6]
        }
      },
      yAxisSeries: []
    };
  },
  mounted() {
    this.refreshDiagram();
  },

  methods: {
    refreshDiagram() {
      if (this.selectedStatement === undefined || this.selectedStatement === null) return;

      if (this.detailedDiagram) this.defineDiagramSettings();
      else this.defineSummaryDiagramSettings();
    },
    defineDiagramSettings() {
      const resourceTypes = [ResourceTypeEnum.Proven, ResourceTypeEnum.Probable, ResourceTypeEnum.Measured, ResourceTypeEnum.Indicated, ResourceTypeEnum.Inferred, ResourceTypeEnum.PotentialMineral];
      this.calculateDiagramSettings(resourceTypes, this.getStatementStats, "MRMR.ResourceType", this.tableData);
    },
    defineSummaryDiagramSettings() {
      const resourceCategories = [ResourceCategoryEnum.Reserve, ResourceCategoryEnum.Resource, ResourceCategoryEnum.Inferred, ResourceCategoryEnum.PotentialMineral];
      this.calculateDiagramSettings(resourceCategories, this.getSummaryStatementStats, "MRMR.ResourceCategories");
    },
    calculateDiagramSettings(types, statsFunc, localizationPrefix, tableDataArray) {
      tableDataArray = [];
      const miningTypes = [];
      if (this.undergroundSelected) {
        miningTypes.push(MiningTypeEnum.Underground)
        tableDataArray.push("");
      }

      if (this.openPitSelected) {
        miningTypes.push(MiningTypeEnum.OpenPit)
      }
      const categories = [];
      const zeroValues = [];
      const plus10Values = [];
      const minus10Values = [];
      const selectedOZ = [];
      const comparedOz = [];
      const ozVariance = [];
      //const tableData = [];

      types.forEach(type => {
        categories.push(this.$t(localizationPrefix + "." + type));

        let selectedOZValue = 0;
        //let selectedGradeValue = 0;
        let comparisonOZValue = 0;
        //let comparisonGradeValue = 0;

        const selectedStatementValues  = statsFunc(this.selectedStatement, type, miningTypes);
        selectedOZValue = selectedStatementValues.ozValue;
        //selectedGradeValue = selectedStatementValues.gradeValue;

        selectedOZ.push(selectedOZValue);


        if (this.comparisonStatement !== undefined && this.comparisonStatement !== null) {
          const comparisonStatementValues  = statsFunc(this.comparisonStatement, type, miningTypes);
          comparisonOZValue = comparisonStatementValues.ozValue;
          //comparisonGradeValue = comparisonStatementValues.gradeValue;

          const varianceOZ = comparisonOZValue === 0 ? selectedOZValue === 0 ? 0 : 100 : (selectedOZValue - comparisonOZValue) * 100 / comparisonOZValue;
          //const varianceGrade = comparisonGradeValue === 0 ? selectedGradeValue === 0 ? 0 : 100 : (selectedGradeValue - comparisonGradeValue) * 100 / comparisonGradeValue;

          comparedOz.push(comparisonOZValue);
          ozVariance.push(varianceOZ);
          zeroValues.push(0);
          plus10Values.push(10);
          minus10Values.push(-10);
        }
      });

      const selectedStatementOzSerie = {
        type: 'bar',
        name:  this.selectedStatement.period.name,
        data: selectedOZ
      }

      const comparedStatementOzSerie = {
        type: 'bar',
        name: this.comparisonStatementName,
        data: comparedOz
      }

      const ozVarianceSerie = {
        type: 'scatter',
        name: "Variance",
        data: ozVariance
      }

      const maxOzVariance =  Math.max.apply(null, ozVariance)

      const zeroSerie = {
        type: 'line',
        name: "0",
        data:  zeroValues
      }

      const plus10Serie = {
        type: 'line',
        name: "+10 %",
        data:  plus10Values
      }

      const minus10Serie = {
        type: 'line',
        name: "-10 %",
        data:  minus10Values
      }

      const ozAxis = [
        {
          seriesName:  this.selectedStatement.period.name,
          tickAmount : 5,
          forceNiceScale : true,
          title: {
            text: this.yAxisLabel
          },
          labels: {
            formatter: (val) => {
              return this.$options.filters.toMRMRTonnage(val)
            }
          },
          min: 0
        },
        {
          seriesName:  this.selectedStatement.period.name,
          show: false,
          labels: {
            formatter: (val) => {
              return this.$options.filters.toMRMRTonnage(val)
            }
          }
        },
        {
          seriesName: "Variance",
          opposite: true,
          show: false,
          labels: {
            formatter: () => {
              return null;
            }
          }
        },
        {
          seriesName: "Variance",
          opposite: true,
          show: false,
          labels: {
            formatter: () => {
              return null;
            }
          }
        },
        {
          seriesName: "Variance",
          opposite: true,
          show: false,
          labels: {
            formatter: () => {
              return null;
            }
          }
        },
        {
          seriesName: "Variance",
          opposite: true,
          title: {
            text: 'Variance (%)'
          },
          labels: {
            formatter: (val) => {
              return this.$options.filters.toIntPercentage(val);
            }
          },
          min: -100,
          max: maxOzVariance > 100 ? maxOzVariance : 100,
          tickAmount: 8
        }
      ]

      const xaxis =  {
        categories
      };

      this.yAxisSeries = [comparedStatementOzSerie, selectedStatementOzSerie, zeroSerie, plus10Serie, minus10Serie, ozVarianceSerie];
      this.$refs.chart.updateOptions({ xaxis, yaxis : ozAxis });
    },
    getStatementStats(statement, type, miningTypes) {
      const stats =  statement.stats.filter(m => m.resourceType === type && miningTypes.includes(m.miningType));
      let tonnageValue = 0;
      let ozValue = 0;
      let gradeValue = 0;
      let gradeTonnage = 0;
      const ownershipPercentage = this.considerOwnershipPercentage ? statement.ownership / 100 : 1;
      if (stats !== undefined);
      {
        stats.forEach(stat => {
          tonnageValue += stat.tonnage * ownershipPercentage;
          if  (this.element === ElementEnum.Gold) {
            ozValue += stat.auOz * ownershipPercentage;
            gradeTonnage += stat.goldGrade * stat.tonnage;
          }
          else if  (this.element === ElementEnum.Silver) {
            ozValue += stat.agOz * ownershipPercentage;
            gradeTonnage += stat.silverGrade * stat.tonnage;
          }
          else if  (this.element === ElementEnum.Zinc) {
            ozValue += stat.znOz * ownershipPercentage;
            gradeTonnage += stat.zincGrade * stat.tonnage;
          }
          else if  (this.element === ElementEnum.Lead) {
            ozValue += stat.pbOz * ownershipPercentage;
            gradeTonnage += stat.leadGrade * stat.tonnage;
          }
          else if  (this.element === ElementEnum.Copper) {
            ozValue += stat.cuOz * ownershipPercentage;
            gradeTonnage += stat.copperGrade * stat.tonnage;
          }
        });

        gradeValue = tonnageValue === 0 ? 0 :  gradeTonnage  / tonnageValue;
      }

      return {
        tonnageValue,
        ozValue,
        gradeValue
      }
    },
    getSummaryStatementStats(statement, category, miningTypes) {
      let types = null;
      switch (category) {
      case ResourceCategoryEnum.Reserve:
        types = [ResourceTypeEnum.Proven, ResourceTypeEnum.Probable];
        break;
      case ResourceCategoryEnum.Resource:
        types = [ResourceTypeEnum.Measured, ResourceTypeEnum.Indicated];
        break;
      case ResourceCategoryEnum.Inferred:
        types = [ResourceTypeEnum.Inferred];
        break;
      case ResourceCategoryEnum.PotentialMineral:
        types = [ResourceTypeEnum.PotentialMineral];
        break;
      default:
        types = [];
      }

      let tonnageValue = 0;
      let ozValue = 0;
      let gradeValue = 0;
      let gradeTonnage = 0;
      const ownershipPercentage = this.considerOwnershipPercentage ? statement.ownership / 100 : 1;

      types.forEach(type => {
        const stats =  statement.stats.filter(m => m.resourceType === type && miningTypes.includes(m.miningType));
        if (stats !== undefined);
        {
          stats.forEach(stat => {
            tonnageValue += stat.tonnage * ownershipPercentage;
            if  (this.element === ElementEnum.Gold) {
              ozValue += stat.auOz * ownershipPercentage;
              gradeTonnage += stat.goldGrade * stat.tonnage;
            }
            else if  (this.element === ElementEnum.Silver) {
              ozValue += stat.agOz * ownershipPercentage;
              gradeTonnage += stat.silverGrade * stat.tonnage;
            }
            else if  (this.element === ElementEnum.Zinc) {
              ozValue += stat.znOz * ownershipPercentage;
              gradeTonnage += stat.zincGrade * stat.tonnage;
            }
            else if  (this.element === ElementEnum.Lead) {
              ozValue += stat.pbOz * ownershipPercentage;
              gradeTonnage += stat.leadGrade * stat.tonnage;
            }
            else if  (this.element === ElementEnum.Copper) {
              ozValue += stat.cuOz * ownershipPercentage;
              gradeTonnage += stat.copperGrade * stat.tonnage;
            }
          });

          gradeValue = tonnageValue === 0 ? 0 :  gradeTonnage  / tonnageValue;
        }
      });

      return {
        tonnageValue,
        ozValue,
        gradeValue
      }
    }
  }
};
</script>
